import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Container, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { KpiRowSkeleton, PageHeaderSkeleton, TableSkeleton } from "../../components/PageSkeletons"
import { api } from "../../lib/api/client"
import { extractErrorMessage, type IEnvelopeResult, unwrapData } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { requireAdmin } from "../../lib/route-guards"
import { apiUrl } from "../../lib/runtime-origin"
import { DiagnosticsView } from "./-diagnostics/DiagnosticsView"
import type {
  IEmbeddingHealth,
  IEventFilters,
  IHealthData,
  IOrphan,
  ISessionEvent,
  ISessionInfo,
} from "./-diagnostics/DiagnosticsView/interfaces"

function DiagnosticsPage(): React.JSX.Element {
  const { i18n } = useLingui()
  const [filters, setFilters] = useState<IEventFilters>({ orgId: "", meetingId: "", severity: null, source: null })
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null)

  const healthQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsHealth(),
    queryFn: async () => {
      const result = await api.api.diagnostics.health.get()
      return unwrapData(result as IEnvelopeResult<IHealthData>)
    },
    refetchInterval: 5000,
  })

  const sessionsQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsSessions(),
    queryFn: async () => {
      const result = await api.api.diagnostics.sessions.get()
      return unwrapData(result as IEnvelopeResult<{ sessions: ISessionInfo[] }>)
    },
    refetchInterval: 5000,
  })

  const orphansQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsOrphans(),
    queryFn: async () => {
      const result = await api.api.diagnostics.orphans.get()
      return unwrapData(result as IEnvelopeResult<{ orphans: IOrphan[] }>)
    },
    refetchInterval: 15000,
  })

  const embeddingHealthQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsEmbeddingHealth(),
    queryFn: async () => {
      // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty doesn't yet pick up the new route at runtime
      const result = await (api.api.diagnostics as any)["embedding-health"].get() // eslint-disable-line @typescript-eslint/no-explicit-any
      return unwrapData(result as IEnvelopeResult<IEmbeddingHealth>)
    },
    refetchInterval: 30000,
  })

  const logsQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsLogs(),
    // Raw fetch intentional: /api/logs is not a typed Eden Treaty endpoint
    queryFn: async (): Promise<{ logs: string[] }> => {
      const res = await fetch(apiUrl("/api/logs?lines=100"))
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      return res.json() as Promise<{ logs: string[] }>
    },
    refetchInterval: 10000,
  })

  const eventsQuery = useQuery({
    queryKey: queryKeys.admin.diagnosticsEvents(filters.orgId, filters.meetingId, filters.severity, filters.source),
    queryFn: async (): Promise<{ events: ISessionEvent[]; total: number }> => {
      const result = await api.api.diagnostics.events.get({
        query: {
          orgId: filters.orgId || undefined,
          meetingId: filters.meetingId || undefined,
          severity: filters.severity ?? undefined,
          source: filters.source ?? undefined,
        },
      })
      return unwrapData(result as IEnvelopeResult<{ events: ISessionEvent[]; total: number }>)
    },
    refetchInterval: 30_000,
  })

  const handleFiltersChange = useCallback((patch: Partial<IEventFilters>): void => {
    setFilters((prev) => ({ ...prev, ...patch }))
  }, [])

  const handleToggleEvent = useCallback((id: number): void => {
    setExpandedEventId((prev) => (prev === id ? null : id))
  }, [])

  // Recovery re-runs the processing pipeline for a stuck meeting. It was
  // previously fire-and-forget (the promise was discarded), so a failure was
  // invisible and there was no progress feedback. Run it through a mutation so
  // the button can show `loading` and surface success/failure as a toast.
  const recoverMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await api.api.diagnostics.orphans({ id }).recover.post()
      if (error) throw new Error(extractErrorMessage(error))
    },
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Recovery started`),
        message: i18n._(msg`Processing is being re-run for this meeting.`),
        color: "success",
      })
      void orphansQuery.refetch()
    },
    onError: (err) => notifyError(err),
  })

  const handleRecoverOrphan = useCallback(
    (id: string): void => {
      // Confirm first — recovery is a side-effectful re-run, not a read.
      modals.openConfirmModal({
        title: i18n._(msg`Recover this meeting?`),
        children: <Text size="sm">{i18n._(msg`This re-runs processing for this meeting.`)}</Text>,
        labels: { confirm: i18n._(msg`Recover`), cancel: i18n._(msg`Cancel`) },
        onConfirm: () => recoverMutation.mutate(id),
      })
    },
    [i18n, recoverMutation],
  )

  if (healthQuery.isLoading) {
    return (
      <Container size="lg" py="xl">
        <PageHeaderSkeleton withActions={false} />
        <KpiRowSkeleton count={2} />
        <TableSkeleton rows={6} cols={4} />
      </Container>
    )
  }

  if (healthQuery.error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="danger" title={i18n._(msg`Access Denied`)}>
          <Trans>Admin access required for diagnostics.</Trans>
        </Alert>
      </Container>
    )
  }

  return (
    <DiagnosticsView
      health={healthQuery.data}
      embedding={embeddingHealthQuery.data}
      sessions={sessionsQuery.data?.sessions ?? []}
      orphans={orphansQuery.data?.orphans ?? []}
      logs={logsQuery.data?.logs ?? []}
      events={eventsQuery.data?.events ?? []}
      eventsTotal={eventsQuery.data?.total ?? 0}
      filters={filters}
      expandedEventId={expandedEventId}
      recoveringOrphanId={recoverMutation.isPending ? (recoverMutation.variables ?? null) : null}
      onFiltersChange={handleFiltersChange}
      onToggleEvent={handleToggleEvent}
      onRecoverOrphan={handleRecoverOrphan}
      onRefetchLogs={() => logsQuery.refetch()}
      onRefetchEvents={() => eventsQuery.refetch()}
    />
  )
}

export const Route = createFileRoute("/_auth/diagnostics")({
  beforeLoad: requireAdmin,
  component: DiagnosticsPage,
})
